import { useState, useMemo, useEffect } from 'react';
import { useFormContext, useFormState, Controller } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TranslateIcon from '@mui/icons-material/TranslateRounded';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { MultiSelect } from '@components/MultiSelect';
import { Switch } from '@components/Switch';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import { AIAssistantLoadingWrapper } from '@features/AIAssistant';
import { useTranslation, useLocations } from '@hooks';
import { CURRENCY_OPTIONS } from '@constants/currencies';
import { CUISINES, MENU_TEXT_LIMITS } from '@constants/menus';
import { LocationType } from '@constants/companies';
import { getLocalizedValue, setLocalizedValue, buildMenuAiContext } from '../helpers';
import DescriptionAssistant from './DescriptionAssistant';
import MenuLocalizeModal from './MenuLocalizeModal';

const Form = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridGap: 16,
    marginBottom: 16,
    '@media (max-width: 1200px)': {
        gridTemplateColumns: '100%',
    },
});

const isMenuLocation = location => [LocationType.Restaurant, LocationType.Hotel].includes(location.type);

const General = ({ isView, defaultLanguage }) => {
    const [localizeOpen, setLocalizeOpen] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    const { setValue, watch, control } = useFormContext();
    const { touchedFields } = useFormState({ control });
    const { t } = useTranslation();
    const { getWritableSelectLocationsOptions, hasSingleLocation, singleLocationId } = useLocations();

    const internalName = watch('internalName');
    const document = watch('document');
    const cuisines = watch('cuisines');
    const publishExternally = watch('publishExternally');
    const locationIds = watch('locationIds');

    const cuisineOptions = useMemo(
        () => CUISINES.map(value => ({ value, label: t(`MenusPage.cuisineValues.${value}`) })),
        [t],
    );

    const groupedLocationOptions = useMemo(
        () => getWritableSelectLocationsOptions('microsites', isMenuLocation),
        [getWritableSelectLocationsOptions],
    );
    const flatLocationOptions = useMemo(
        () => groupedLocationOptions.flatMap(group => group.children),
        [groupedLocationOptions],
    );

    const autoSelectSingleLocation =
        hasSingleLocation && !isView && flatLocationOptions.some(o => o.value === singleLocationId);

    useEffect(() => {
        if (autoSelectSingleLocation) {
            setValue('locationIds', [singleLocationId], { shouldValidate: true });
        }
    }, [autoSelectSingleLocation, setValue, singleLocationId]);

    const name = getLocalizedValue(document?.names, defaultLanguage);
    const description = getLocalizedValue(document?.descriptions, defaultLanguage);

    const onChangeLocalized = (field, value) => {
        setValue(
            'document',
            { ...document, [field]: setLocalizedValue(document?.[field], defaultLanguage, value) },
            { shouldDirty: true, shouldTouch: true },
        );
    };

    const hasTitle = (document?.names || []).some(n => !!n.value?.trim());
    const descriptionTooLong = (description || '').length > MENU_TEXT_LIMITS.description;
    const internalNameError = !!touchedFields.internalName && !internalName?.trim();
    const titleError = !!touchedFields.document && !hasTitle;
    const locationsError = !!touchedFields.locationIds && !(locationIds || []).length;

    return (
        <>
            <Form>
                <Input
                    inputProps={{
                        value: internalName,
                        onChange: e =>
                            setValue('internalName', e.target.value, { shouldDirty: true, shouldTouch: true }),
                    }}
                    label={t('MenusPage.internalName')}
                    error={internalNameError}
                    helperText={internalNameError ? t('validation.required') : t('MenusPage.internalNameHint')}
                    fullWidth
                    required
                    disabled={isView}
                />
                <Controller
                    control={control}
                    name="currencyCode"
                    render={({ field }) => (
                        <Select
                            {...field}
                            onChange={e => setValue('currencyCode', e.target.value, { shouldDirty: true })}
                            label={t('common.currency')}
                            fullWidth
                            required
                            options={CURRENCY_OPTIONS}
                            disabled={isView}
                        />
                    )}
                />
                {!autoSelectSingleLocation && (
                    <MultiSelect
                        value={flatLocationOptions.filter(o => (locationIds || []).includes(o.value))}
                        onChange={selected =>
                            setValue(
                                'locationIds',
                                selected.map(o => o.value),
                                {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                },
                            )
                        }
                        options={groupedLocationOptions}
                        groupBased
                        label={t('MenusPage.locations')}
                        countable
                        required
                        error={locationsError}
                        style={{ gridColumn: '1 / -1' }}
                        disabled={isView}
                    />
                )}
                <Input
                    inputProps={{
                        value: name,
                        onChange: e => onChangeLocalized('names', e.target.value),
                    }}
                    label={t('common.title')}
                    error={titleError}
                    helperText={titleError ? t('validation.required') : ''}
                    fullWidth
                    required
                    disabled={isView}
                />
                <MultiSelect
                    value={cuisines || []}
                    onChange={selected => setValue('cuisines', selected, { shouldDirty: true })}
                    options={cuisineOptions}
                    label={t('MenusPage.cuisines')}
                    countable
                    disabled={isView}
                />
                <div style={{ position: 'relative', gridColumn: '1 / -1' }}>
                    {aiLoading && <AIAssistantLoadingWrapper />}
                    <Input
                        inputProps={{
                            value: description,
                            onChange: e => onChangeLocalized('descriptions', e.target.value),
                        }}
                        label={t('common.description')}
                        fullWidth
                        multiline
                        minRows={3}
                        error={descriptionTooLong}
                        helperText={descriptionTooLong ? t('MenusPage.issueDescriptionLength') : ''}
                        disabled={isView || aiLoading}
                        toolbar={
                            isView ? undefined : (
                                <DescriptionAssistant
                                    value={description}
                                    setValue={newValue => onChangeLocalized('descriptions', newValue)}
                                    context={buildMenuAiContext(document, cuisines, defaultLanguage)}
                                    disabled={aiLoading}
                                    setLoading={setAiLoading}
                                />
                            )
                        }
                    />
                </div>
            </Form>
            <Box sx={{ marginBottom: '16px' }}>
                <Button
                    variant="outlined"
                    startIcon={<TranslateIcon />}
                    onClick={() => setLocalizeOpen(true)}
                    disabled={isView}
                >
                    {t('buttons.localization')}
                </Button>
            </Box>
            <Switch
                checked={publishExternally}
                handleChange={e => setValue('publishExternally', e.target.checked, { shouldDirty: true })}
                label={t('MenusPage.showOnGoogle')}
                disabled={isView}
            />
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block', marginTop: '4px' }}>
                {t('MenusPage.showOnGoogleHint')}
            </Typography>
            {localizeOpen ? (
                <MenuLocalizeModal
                    isOpen
                    title={t('MenusPage.menuLocalization')}
                    names={document?.names || []}
                    descriptions={document?.descriptions || []}
                    aiContext={buildMenuAiContext(document, cuisines, defaultLanguage)}
                    defaultLanguage={defaultLanguage}
                    onClose={() => setLocalizeOpen(false)}
                    onSave={(names, descriptions) => {
                        setValue(
                            'document',
                            { ...document, names, descriptions },
                            { shouldDirty: true, shouldTouch: true },
                        );
                        setLocalizeOpen(false);
                    }}
                />
            ) : null}
        </>
    );
};

export default General;
